import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BadgePercent, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PublicNavbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const links = [
    { to: "/how-it-works", label: "How it works" },
    { to: "/categories", label: "Categories" },
    { to: "/for-merchants", label: "For merchants" },
    { to: "/faq", label: "FAQ" },
  ];

  return (
    <header className="sticky top-0 z-40 w-full bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
          <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center">
            <BadgePercent className="w-5 h-5" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight text-gray-900">
            LoyaltyCircle <span className="text-emerald-700">AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-emerald-700" : "text-gray-700 hover:text-gray-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Button
              data-testid="open-app-btn"
              onClick={() => nav(user.role === "consumer" ? "/app" : "/admin")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-5"
            >
              Open app
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                data-testid="public-login-btn"
                onClick={() => nav("/login")}
                className="text-gray-700"
              >
                Log in
              </Button>
              <Button
                data-testid="public-signup-btn"
                onClick={() => nav("/signup")}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-5"
              >
                Get cashback
              </Button>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-800"
              >
                {l.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              {user ? (
                <Button
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => nav(user.role === "consumer" ? "/app" : "/admin")}
                >
                  Open app
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => nav("/login")}>
                    Log in
                  </Button>
                  <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => nav("/signup")}>
                    Get cashback
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
